import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapSettings,
  type MapSettingsGearMode,
} from '@/components/primitives/MapSettings';
import type { MapSettings as MapSettingsState, MapSettingsOptions } from '@/hooks/useMapSettings';
import { MapMaximize } from '@/components/primitives/MapMaximize';
import { ParcelOverlay } from '@/components/maps/ParcelOverlay';
import { MapSearchControl } from '@/components/maps/MapSearchControl';
import type { ParcelRecord, ParcelStatus } from '@/utils/parcelGeometry';
import {
  BoundariesOverlay,
  type BoundaryRecord,
} from '@/components/maps/BoundariesOverlay';
import { WaterSourceOverlay } from '@/components/maps/WaterSourceOverlay';
import { LocationMarker } from '@/components/maps/LocationMarker';
import {
  MapDrawControl,
  type DrawnEvent,
} from '@/components/maps/MapDrawControl';
import {
  FitBoundsToData,
  type MarkerHint,
  type PolygonHint,
} from '@/components/maps/FitBoundsToData';
import {
  BoundsObserver,
  type MapBounds,
} from '@/components/maps/BoundsObserver';
import { EnsureMapSize } from '@/components/maps/EnsureMapSize';
import { useMapSettings } from '@/hooks/useMapSettings';
import { cn } from '@/lib/utils';

type Variant = 'inline' | 'lg' | 'screen';

const variantHeight: Record<Variant, string> = {
  inline: 'h-[50vh] min-h-[320px]',
  lg: 'h-[70vh] min-h-[420px]',
  screen: 'h-[calc(100vh-6rem)]',
};

export interface MapCanvasDrawingConfig {
  enabled: boolean;
  /** Toolbar position. Defaults to top-right. */
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  initialGeometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  drawOptions?: object;
  editOptions?: object;
  onCreated?: (e: DrawnEvent) => void;
  onEdited?: (e: DrawnEvent) => void;
  onDeleted?: (e: { raw: L.LeafletEvent; allCleared: boolean }) => void;
  onDrawingStateChange?: (drawing: boolean) => void;
  /** Escape hatch: receive the FeatureGroup ref for imperative seeding. */
  featureGroupRef?: MutableRefObject<L.FeatureGroup | null>;
}

export interface MapCanvasLocationConfig {
  position: [number, number] | null;
  onChange: (next: [number, number]) => void;
  enabled?: boolean;
}

interface MapCanvasProps {
  center: [number, number];
  zoom: number;

  /** Shell height. Defaults to `'inline'`. Ignored when maximized. */
  variant?: Variant;

  /** Class applied to the outer (non-maximized) wrapper. */
  className?: string;

  /**
   * Settings gear behaviour:
   * - `'full'` (default): every row including parcels/boundaries (staff-gated)
   * - `'basic'`: tile layer + pins/polygons (+ wind), no tenant overlays
   * - `'none'`: no gear at all
   */
  gearMode?: MapSettingsGearMode;

  /** Show the wind toggle inside the gear. */
  showWindToggle?: boolean;

  /** Hide the maximize button. Default `true` (button visible). */
  maximize?: boolean;

  /**
   * Show the address search icon (top-right chrome, alongside maximize/gear).
   * Searches the tenant's already-loaded parcels by street name/house number
   * — no effect unless `tenantId` is set and parcels are loaded. Default off.
   */
  mapSearch?: boolean;

  /**
   * Per-tenant overlay scope. Required for parcels/boundaries to fetch.
   * Pass `null` when there's no tenant selected — the overlays render
   * nothing and the gear shows the appropriate disabled tooltip.
   */
  tenantId?: number | null;

  /**
   * Rendered inside MapContainer — caller's markers/polygons/geojson.
   *
   * Pass plain JSX for static overlays, or a function `({ settings,
   * maximized }) => ReactNode` to gate rendering on the persisted map
   * settings (e.g. `settings.showPins ? <Marker .../> : null`). The
   * render-prop form is how `WildfiresIndex` and `ForecastMap` keep their
   * marker/wind toggles in sync with the gear without holding a duplicate
   * `useMapSettings` instance that would desync from MapCanvas's own.
   */
  children?:
    | ReactNode
    | ((state: { settings: MapSettingsState; maximized: boolean }) => ReactNode);

  /** Rendered as an absolute-positioned slot top-left (e.g. forecast badges). */
  topLeftSlot?: ReactNode;

  /** Rendered as an absolute-positioned slot bottom-right (e.g. map legend). */
  legend?: ReactNode;

  /**
   * Rendered as a block underneath the map but INSIDE the maximize wrapper
   * — so it stays visible when the map is maximized to fullscreen. Use for
   * persistent controls that should travel with the map (e.g. ForecastMap's
   * hour slider). Receives `maximized` so the caller can adjust styling.
   */
  footerSlot?: ReactNode | ((state: { maximized: boolean }) => ReactNode);

  /** Opt-in modes. */
  drawing?: MapCanvasDrawingConfig;
  locationPicker?: MapCanvasLocationConfig;

  /** Force the parcels overlay on regardless of the gear toggle — for pages
   *  that are *about* parcels (e.g. the admin parcels map). */
  forceParcels?: boolean;
  /** Force the boundaries overlay on regardless of the gear toggle. */
  forceBoundaries?: boolean;
  /** Boundary colour scheme forwarded to `BoundariesOverlay`. */
  boundaryColorMode?: 'semantic' | 'indexed';
  /** ref → hazard tags, forwarded to ParcelOverlay for hazard-coloured labels. */
  parcelHazardByRef?: ReadonlyMap<string, string[]> | null;
  /** ref → membership/payment status, forwarded to ParcelOverlay for the
   *  parcel colour ramp. Omit and the overlay fetches its own. */
  parcelStatusByRef?: ReadonlyMap<string, ParcelStatus> | null;
  /** A single parcel ref to highlight with the purple outline style. */
  highlightRef?: string | null;
  /** Restrict parcels to these boundary slugs (client-side per-boundary filter). */
  visibleBoundaryNames?: ReadonlySet<string> | null;
  /** Page-owned parcel selection — receives the clicked parcel so the page can
   *  render its own UI (e.g. a detail drawer); suppresses the default popup. */
  onParcelSelect?: (parcel: ParcelRecord, latlng: L.LatLng) => void;
  /** Receives the loaded linked-ref set (lets a page avoid a double fetch). */
  onLinkedRefsLoaded?: (refs: Set<string>) => void;
  /** Receives the total fetched parcel count (for page-level summaries). */
  onParcelsLoaded?: (count: number) => void;
  /** Bump to re-fetch the parcel link set after a link/unlink. */
  parcelRefreshKey?: number;
  /** When false, parcels render but are non-interactive (no click/popup).
   *  Auto-forced off while a draw/edit control is mounted. Defaults to true. */
  parcelInteractive?: boolean;

  /** Auto-fit to data on mount/data-change. Default `false` — opt-in. */
  fitBoundsTo?: { markers?: MarkerHint[]; polygons?: PolygonHint[] } | false;

  /** Receive every viewport pan/zoom (parcel map uses this for clipping). */
  onBoundsChange?: (b: MapBounds) => void;

  /**
   * Convenience: when this prop's array identity changes, call
   * `map.setView(recenterTo)` once. Use `onMapReady` for full control.
   */
  recenterTo?: [number, number] | null;

  /** Capture the L.Map instance for imperative work (locate, fitBounds, …). */
  onMapReady?: (map: L.Map) => void;

  /** Forwarded to the underlying MapContainer. */
  preferCanvas?: boolean;

  /** Forwarded to the underlying MapContainer. */
  scrollWheelZoom?: boolean;

  /** Optional id on the outer wrapper (handy for tests). */
  id?: string;

  /**
   * Page-local map-settings seed. Unlike `forceParcels`/`forceBoundaries`,
   * which pin an overlay on and ignore the gear, this sets a *default* the
   * user can still change — and keeps the listed keys out of the shared
   * localStorage so one page's preference can't leak to another's map.
   * See `MapSettingsOptions`.
   */
  mapSettings?: MapSettingsOptions;
}

const TILE_STREET = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

const TILE_SATELLITE = {
  url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  attribution: 'Tiles &copy; Esri',
};

/**
 * Tiny helper that fires `onMapReady` once the MapContainer's map instance
 * is available. Must live inside the MapContainer because `useMap` only
 * works in that subtree.
 */
function MapReady({ onReady }: { onReady: (map: L.Map) => void }) {
  const map = useMap();
  useEffect(() => {
    if (map) onReady(map);
  }, [map, onReady]);
  return null;
}

/**
 * Recenter the map whenever `target` changes to a NEW value. Skips when
 * `target` is null.
 *
 * The applied-value dedupe matters when a caller toggles `recenterTo` between
 * `null` and a fixed center (e.g. the wizard's `drawing ? null : mapCenter`,
 * which pauses recentering during a draw). Without it, the `null → center`
 * flip re-runs `setView` and yanks the view back to that center right after
 * the user drops a pin / finishes a polygon — discarding wherever they had
 * panned. Re-applying the same center is a no-op here.
 */
function Recenter({ target }: { target: [number, number] | null | undefined }) {
  const map = useMap();
  const appliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!map || !target) return;
    const key = `${target[0]},${target[1]}`;
    if (appliedRef.current === key) return;
    appliedRef.current = key;
    try {
      map.setView(target, map.getZoom());
    } catch (err) {
      console.warn('MapCanvas: setView failed', err);
    }
  }, [map, target?.[0], target?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * Canonical map composition for the app. Wraps `react-leaflet`'s
 * `MapContainer` with the shared chrome (settings gear, maximize toggle,
 * persisted tile layer / overlay preferences via `useMapSettings`) and
 * opt-in modes for drawing, location picking, bounds tracking, and legends.
 *
 * Caller renders its own markers/polygons/geojson as children — no array
 * props. See `MapCanvasProps` for the full API.
 *
 * Replaces the legacy 1,400-line `MapComponent.jsx` monolith.
 */
export const MapCanvas = forwardRef<L.Map | null, MapCanvasProps>(
  function MapCanvas(
    {
      center,
      zoom,
      variant = 'inline',
      className,
      gearMode = 'full',
      showWindToggle,
      maximize = true,
      mapSearch = false,
      tenantId,
      children,
      topLeftSlot,
      legend,
      footerSlot,
      drawing,
      locationPicker,
      forceParcels,
      forceBoundaries,
      boundaryColorMode,
      parcelHazardByRef,
      parcelStatusByRef,
      highlightRef,
      visibleBoundaryNames,
      onParcelSelect,
      onLinkedRefsLoaded,
      onParcelsLoaded,
      parcelRefreshKey,
      parcelInteractive,
      fitBoundsTo,
      onBoundsChange,
      recenterTo,
      onMapReady,
      preferCanvas = true,
      scrollWheelZoom = true,
      id,
      mapSettings,
    },
    _mapRef,
  ) {
    const {
      settings,
      update: updateSettings,
      getEnabledBoundaryIds,
      toggleBoundary,
    } = useMapSettings(mapSettings);
    const [boundaries, setBoundaries] = useState<BoundaryRecord[]>([]);
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
    const [searchParcels, setSearchParcels] = useState<ParcelRecord[]>([]);

    const includeTenantOverlays = gearMode === 'full';
    const enabledBoundaryIds = useMemo(
      () => (includeTenantOverlays ? getEnabledBoundaryIds(tenantId ?? null) : null),
      [getEnabledBoundaryIds, tenantId, includeTenantOverlays],
    );

    // Parcels respect the same per-boundary selection as the boundary outlines:
    // derive the visible boundary slugs from the gear's enabled set so toggling
    // a boundary off hides its parcels too. A page-supplied `visibleBoundaryNames`
    // overrides this.
    const derivedVisibleBoundaryNames = useMemo(() => {
      // Show all until we have both a selection and the loaded boundary list
      // (avoids hiding every parcel during the boundary fetch).
      if (!enabledBoundaryIds || boundaries.length === 0) return null;
      const set = new Set<string>();
      boundaries.forEach((b) => {
        if (enabledBoundaryIds.has(b.id) && b.boundary_name) set.add(b.boundary_name);
      });
      return set;
    }, [boundaries, enabledBoundaryIds]);

    const tileConfig = settings.tileLayer === 'satellite' ? TILE_SATELLITE : TILE_STREET;

    const onToggleBoundary = useCallback(
      (bid: number) => {
        if (!tenantId) return;
        toggleBoundary(
          tenantId,
          bid,
          boundaries.map((b) => b.id),
        );
      },
      [tenantId, toggleBoundary, boundaries],
    );

    return (
      <MapMaximize>
        {({ maximized, button: maximizeButton }) => {
          const childContent =
            typeof children === 'function'
              ? children({ settings, maximized })
              : children;
          const footerContent =
            typeof footerSlot === 'function' ? footerSlot({ maximized }) : footerSlot;
          // When there's a footer the map area needs to share the box with
          // it. Maximized: both stack inside the fullscreen overlay (map
          // grows, footer is fixed-height). Not maximized: map uses its
          // variant height, footer flows underneath.
          const mapAreaClass = cn(
            'relative w-full overflow-hidden border border-border bg-card',
            // `isolate` creates a new stacking context so Leaflet's internal
            // z-indexes (200–700) don't bleed out and paint over the mobile
            // bottom nav or other fixed elements.
            'isolate',
            maximized
              ? 'flex-1 rounded-none border-0'
              : cn('rounded-lg', variantHeight[variant]),
            !maximized && !footerContent && className,
          );
          const innerMap = (
          <div
            id={!footerContent ? id : undefined}
            className={mapAreaClass}
          >
            <MapContainer
              center={center}
              zoom={zoom}
              style={{ height: '100%', width: '100%' }}
              preferCanvas={preferCanvas}
              scrollWheelZoom={scrollWheelZoom}
              attributionControl
            >
              <TileLayer attribution={tileConfig.attribution} url={tileConfig.url} />

              <EnsureMapSize />

              {/* Tenant-scoped overlays only mount in 'full' mode and only
                  when a tenantId is set; in 'basic'/'none' modes we skip
                  the fetches entirely so anonymous maps don't 401. */}
              {includeTenantOverlays && (
                <>
                  <BoundariesOverlay
                    tenantId={tenantId ?? null}
                    enabled={Boolean(forceBoundaries) || settings.showBoundaries}
                    enabledIds={enabledBoundaryIds}
                    onBoundariesLoaded={setBoundaries}
                    colorMode={boundaryColorMode}
                    /* When parcels are shown and tappable, make boundaries
                       non-interactive so clicks fall through to the parcel
                       layer (boundary path sits above the parcel canvas and
                       would otherwise swallow the click). */
                    interactive={
                      !(
                        (Boolean(forceParcels) || settings.showParcels) &&
                        parcelInteractive !== false &&
                        !drawing?.enabled
                      )
                    }
                  />
                  <ParcelOverlay
                    tenantId={tenantId ?? null}
                    enabled={Boolean(forceParcels) || settings.showParcels}
                    showLabels={settings.showParcelNo}
                    hazardByRef={parcelHazardByRef}
                    statusByRef={parcelStatusByRef}
                    highlightRef={highlightRef}
                    visibleBoundaryNames={visibleBoundaryNames ?? derivedVisibleBoundaryNames}
                    onParcelSelect={onParcelSelect}
                    onLinkedRefsLoaded={onLinkedRefsLoaded}
                    onParcelsLoaded={onParcelsLoaded}
                    onParcelsData={mapSearch ? setSearchParcels : undefined}
                    refreshKey={parcelRefreshKey}
                    interactive={parcelInteractive !== false && !drawing?.enabled}
                  />
                  {settings.showWaterSources && tenantId && (
                    <WaterSourceOverlay tenantId={tenantId} />
                  )}
                </>
              )}

              {childContent}

              {locationPicker && (
                <LocationMarker
                  position={locationPicker.position}
                  onChange={locationPicker.onChange}
                  enabled={locationPicker.enabled ?? true}
                />
              )}

              {drawing?.enabled && (
                <MapDrawControl
                  /* Default to top-left so the draw/edit/delete toolbar clears
                     the gear + maximize chrome (top-right). */
                  position={drawing.position ?? 'topleft'}
                  initialGeometry={drawing.initialGeometry ?? null}
                  drawOptions={drawing.drawOptions}
                  editOptions={drawing.editOptions}
                  onCreated={drawing.onCreated}
                  onEdited={drawing.onEdited}
                  onDeleted={drawing.onDeleted}
                  onDrawingStateChange={drawing.onDrawingStateChange}
                  featureGroupRef={drawing.featureGroupRef}
                />
              )}

              {fitBoundsTo && (
                <FitBoundsToData
                  markers={fitBoundsTo.markers}
                  polygons={fitBoundsTo.polygons}
                />
              )}

              {onBoundsChange && <BoundsObserver onBoundsChange={onBoundsChange} />}

              {/* Always mounted so its applied-value dedupe (appliedRef)
                  survives callers toggling recenterTo null↔center during a
                  draw. Conditionally mounting it would remount fresh on each
                  null→center flip and re-fire setView, snapping the view back
                  to the initial center right after a pin/polygon is added.
                  Recenter no-ops on a null target. */}
              <Recenter target={recenterTo ?? null} />

              {onMapReady && <MapReady onReady={onMapReady} />}
              {mapSearch && <MapReady onReady={setMapInstance} />}
            </MapContainer>

            {/* Top-left slot — caller-owned. Useful for forecast badges,
                draw status banners, etc. */}
            {topLeftSlot && (
              <div className="pointer-events-none absolute left-3 top-3 z-[450] flex flex-col gap-1.5">
                {topLeftSlot}
              </div>
            )}

            {/* Top-right chrome — search icon + maximize button + settings gear. */}
            <div className="absolute right-3 top-3 z-[450] flex flex-col gap-2">
              {mapSearch && (
                <MapSearchControl
                  tenantId={tenantId ?? null}
                  parcels={searchParcels}
                  map={mapInstance}
                  onParcelSelect={onParcelSelect}
                />
              )}
              {maximize && maximizeButton}
              {gearMode !== 'none' && (
                <MapSettings
                  settings={settings}
                  onChange={updateSettings}
                  gearMode={gearMode}
                  showWindToggle={showWindToggle}
                  parcelsDisabledReason={
                    includeTenantOverlays && tenantId == null
                      ? 'Select an FPA in the top bar to see parcels.'
                      : undefined
                  }
                  boundariesDisabledReason={
                    includeTenantOverlays && tenantId == null
                      ? 'Select an FPA in the top bar to see boundaries.'
                      : undefined
                  }
                  boundaries={includeTenantOverlays ? boundaries : undefined}
                  enabledBoundaryIds={enabledBoundaryIds}
                  onToggleBoundary={includeTenantOverlays ? onToggleBoundary : undefined}
                />
              )}
            </div>

            {/* Bottom-right slot — typically the map legend. */}
            {legend && (
              <div className="pointer-events-none absolute bottom-3 right-3 z-[450]">
                <div className="pointer-events-auto">{legend}</div>
              </div>
            )}
          </div>
          );
          if (!footerContent) return innerMap;
          // Footer present — wrap map + footer in a flex column so they
          // stack inside the maximize wrapper (map flexes to fill, footer
          // sticks to the bottom). Outer class merging respects the
          // caller's `className` only when not maximized.
          return (
            <div
              id={id}
              className={cn(
                'flex flex-col',
                maximized ? 'h-full' : '',
                !maximized && className,
              )}
            >
              {innerMap}
              {footerContent}
            </div>
          );
        }}
      </MapMaximize>
    );
  },
);

export default MapCanvas;

/**
 * Re-export `useMapSettings` for callers that need to read the persisted
 * settings outside the canvas (e.g. ForecastMap reads `showWind` to gate
 * its own arrow marker child). Avoids forcing every consumer to import
 * from `@/hooks/useMapSettings` separately.
 */
export { useMapSettings };
